import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from "react";
import axios from "axios";
import {
  FileText,
  Image as ImageIcon,
  File as FileIcon,
  Download,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Loader2,
  AlertTriangle,
  FolderOpen,
  Rows3,
  Maximize2,
  Package,
} from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument, StandardFonts } from "pdf-lib";

// PDF.js worker — served from public/ at a stable root path and assigned as
// a PLAIN STRING: no URL resolution at all, immune to every
// module-base shim (2026-07 incident: some production build
// environments inlined a file:///app/... module base, so `new Worker()`
// threw synchronously and the worker was never even requested; the
// fake-worker fallback then imported pdfjs's unbundled module tree). The
// copy in public/ is kept byte-identical to the installed pdfjs-dist by an
// incident test. No CDN; HIPAA: no third-party services touch documents.
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const API = process.env.REACT_APP_BACKEND_URL;

const getStoredToken = () =>
  sessionStorage.getItem("token") || localStorage.getItem("token");

const typeIcon = (contentType) => {
  if (contentType?.startsWith("image/")) return <ImageIcon size={15} className="text-sky-500 flex-shrink-0" />;
  if (contentType === "application/pdf") return <FileText size={15} className="text-rose-500 flex-shrink-0" />;
  if (contentType === "text/plain") return <FileText size={15} className="text-slate-500 flex-shrink-0" />;
  return <FileIcon size={15} className="text-slate-400 flex-shrink-0" />;
};

// Client-side blob save — document bytes NEVER pass through the backend.
const saveBlob = (blob, filename) => {
  const a = document.createElement("a");
  const url = URL.createObjectURL(blob);
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

// Module-scope constant — stable identity across renders. Inline element/
// object props on <Document> are per-render identities: the classic
// react-pdf reload loop (teardown → reload → state update → render → …).
const PDF_LOADING_ELEMENT = (
  <div className="self-center flex items-center gap-2 text-slate-400 text-sm">
    <Loader2 className="animate-spin" size={16} /> Loading PDF…
  </div>
);

// US-Letter aspect ratio used to estimate unrendered page heights.
const PAGE_ASPECT = 11 / 8.5;

// Session key for the stream/focus mode toggle.
const VIEWER_MODE_KEY = "intake-doc-viewer-mode";

// True when the event originated inside a form field — staff type in the
// adjacent detail panel with the viewer open; paging must never steal keys.
// SHARED: the workspace-level Escape handler in ReferralIntake imports this
// exact guard (never duplicated).
export const isFormFieldTarget = (e) => {
  const t = e.target;
  const tag = t?.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    !!t?.isContentEditable
  );
};

// ── Full-packet assembly (client-side only; pdf-lib) ────────────────────────
// PDF pages copied, images embedded as pages, text rendered to pages —
// prescription-first order preserved by the caller; failed/unsupported items
// are skipped and reported, never abort the packet.

const PACKET_TEXT_WRAP = 95;
const PACKET_LINES_PER_PAGE = 46;

export const wrapPacketText = (text) => {
  const out = [];
  (text || "").split("\n").forEach((raw) => {
    let line = raw.replace(/\t/g, "  ");
    if (line === "") {
      out.push("");
      return;
    }
    while (line.length > PACKET_TEXT_WRAP) {
      let cut = line.lastIndexOf(" ", PACKET_TEXT_WRAP);
      if (cut <= 0) cut = PACKET_TEXT_WRAP;
      out.push(line.slice(0, cut));
      line = line.slice(cut).trimStart();
    }
    out.push(line);
  });
  return out;
};

export const assemblePacket = async (items) => {
  const out = await PDFDocument.create();
  const font = await out.embedFont(StandardFonts.Helvetica);
  const skipped = [];
  for (const item of items) {
    try {
      if (item.kind === "pdf") {
        const src = await PDFDocument.load(item.bytes);
        const copied = await out.copyPages(src, src.getPageIndices());
        copied.forEach((pg) => out.addPage(pg));
      } else if (item.kind === "image") {
        const img =
          item.contentType === "image/png"
            ? await out.embedPng(item.bytes)
            : await out.embedJpg(item.bytes);
        const page = out.addPage([img.width, img.height]);
        page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height });
      } else if (item.kind === "text") {
        const lines = wrapPacketText(item.text);
        for (let i = 0; i < Math.max(lines.length, 1); i += PACKET_LINES_PER_PAGE) {
          const page = out.addPage([612, 792]);
          lines.slice(i, i + PACKET_LINES_PER_PAGE).forEach((ln, j) => {
            // pdf-lib WinAnsi cannot encode arbitrary unicode — replace.
            const safe = ln.replace(/[^\x20-\x7E]/g, "?");
            page.drawText(safe, { x: 36, y: 756 - j * 15.5, size: 10, font });
          });
        }
      } else {
        skipped.push(item.name);
      }
    } catch {
      skipped.push(item.name);
    }
  }
  return { bytes: await out.save(), skipped };
};

// ── Lazy page — renders a real <Page> only when scrolled near the viewport
// (IntersectionObserver, 600px lookahead). A 40-page packet must never
// eagerly render 40 canvases. Once rendered a page stays rendered (no
// scroll-jump churn).
const LazyPage = ({ docId, pageNumber, width, onVisibility }) => {
  const ref = useRef(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) setNear(true);
          onVisibility?.(docId, pageNumber, en.isIntersecting);
        });
      },
      { rootMargin: "600px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [docId, pageNumber, onVisibility]);
  return (
    <div
      ref={ref}
      style={near ? undefined : { minHeight: Math.round(width * PAGE_ASPECT) }}
      className="mb-3 flex justify-center"
      data-testid={`lazy-page-${docId}-${pageNumber}`}
    >
      {near ? (
        <Page
          pageNumber={pageNumber}
          width={width}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          className="shadow-md"
        />
      ) : null}
    </div>
  );
};

// Text block (Email Body) for the stream — fetch once per mount.
const TextBlock = ({ doc, onError }) => {
  const [text, setText] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    fetch(doc.url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((t) => {
        if (!cancelled) setText(t);
      })
      .catch(() => {
        if (!cancelled) onError();
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [doc.url, onError]);
  if (busy) {
    return (
      <div className="flex items-center gap-2 text-slate-400 text-sm justify-center py-6">
        <Loader2 className="animate-spin" size={16} /> Loading…
      </div>
    );
  }
  return (
    <pre
      className="w-full bg-white border border-slate-200 rounded-lg p-4 text-xs font-mono text-slate-800 whitespace-pre-wrap overflow-auto"
      data-testid="doc-text-content"
    >
      {text ?? ""}
    </pre>
  );
};

// One document's slot in the continuous stream: slim labeled divider,
// then the document body (all PDF pages stacked lazily / image / text).
const DocSection = ({
  doc,
  width,
  numPages,
  failed,
  onLoaded,
  onExpired,
  onEnterFocus,
  onDownload,
  sectionRef,
  onPageVisibility,
  downloadBusy,
}) => {
  const isPdf = doc.content_type === "application/pdf";
  const isImage = doc.content_type?.startsWith("image/");
  const isText = doc.content_type === "text/plain";
  const handleLoad = useCallback(({ numPages: n }) => onLoaded(doc.id, n), [doc.id, onLoaded]);
  const handleError = useCallback(() => onExpired(doc.id), [doc.id, onExpired]);
  // Per-document progressive placeholder — memoized so the <Document>
  // loading prop identity is stable across renders (identity law).
  const loadingEl = useMemo(
    () => (
      <div className="flex items-center gap-2 text-slate-400 text-sm justify-center py-6">
        <Loader2 className="animate-spin" size={16} /> Loading {doc.name}…
      </div>
    ),
    [doc.name]
  );
  return (
    <section ref={sectionRef} data-docid={doc.id} data-testid={`doc-section-${doc.id}`}>
      {/* Slim labeled divider — clicking the name opens focus mode. */}
      <div className="flex items-center gap-2 py-3">
        <div className="flex-1 h-px bg-slate-300" />
        {typeIcon(doc.content_type)}
        <button
          onClick={() => onEnterFocus(doc.id)}
          className="text-xs font-semibold text-slate-600 hover:text-blue-700 truncate max-w-[60%]"
          data-testid={`doc-divider-${doc.id}`}
          title="Open in focus mode"
        >
          — {doc.name} —
        </button>
        <button
          onClick={() => onDownload(doc.id)}
          disabled={downloadBusy}
          className="p-1 rounded hover:bg-slate-200 text-slate-500 disabled:opacity-30"
          data-testid={`doc-section-download-${doc.id}`}
          aria-label={`Download ${doc.name}`}
          title="Download this document"
        >
          <Download size={12} />
        </button>
        <div className="flex-1 h-px bg-slate-300" />
      </div>
      {failed ? (
        // One failed document never blocks the stream — inline notice only.
        <div
          className="mx-auto max-w-md text-center text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
          data-testid={`doc-stream-error-${doc.id}`}
        >
          <AlertTriangle size={14} className="text-amber-500 inline mr-1" />
          Couldn't load {doc.name} — try downloading it
        </div>
      ) : isPdf ? (
        <Document
          file={doc.url}
          onLoadSuccess={handleLoad}
          onLoadError={handleError}
          loading={loadingEl}
        >
          {Array.from({ length: numPages || 0 }, (_, i) => (
            <LazyPage
              key={i + 1}
              docId={doc.id}
              pageNumber={i + 1}
              width={width}
              onVisibility={onPageVisibility}
            />
          ))}
        </Document>
      ) : isImage ? (
        <img
          src={doc.url}
          alt={doc.name}
          onError={handleError}
          className="w-full object-contain shadow-md rounded"
          data-testid="doc-image"
        />
      ) : isText ? (
        <TextBlock doc={doc} onError={handleError} />
      ) : (
        <div className="text-center py-4" data-testid="doc-unsupported">
          <FileIcon size={22} className="text-slate-400 mx-auto mb-1" />
          <p className="text-xs text-slate-600 mb-2">{doc.name}</p>
          <button
            onClick={() => onDownload(doc.id)}
            disabled={downloadBusy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            data-testid="doc-download-unsupported-btn"
          >
            <Download size={12} /> Download
          </button>
        </div>
      )}
    </section>
  );
};

// In-portal GCS document viewer — fills the LEFT PANE of the split-screen
// intake workspace (the workspace grid in ReferralIntake owns placement;
// this component is layout-agnostic: h-full flex column). Signed URLs live
// ONLY in component memory — never in the page URL, query params, logs or
// analytics. Documents auto-load on mount (no button press); the
// documents_viewed audit fires on the listing call exactly as before.
export const DocumentViewerPanel = ({ fileId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  // Stream (continuous scroll, default) vs focus (single document) —
  // persisted for the session.
  const [mode, setMode] = useState(() => {
    try {
      return sessionStorage.getItem(VIEWER_MODE_KEY) === "focus" ? "focus" : "stream";
    } catch {
      return "stream";
    }
  });
  const setModePersist = useCallback((m) => {
    setMode(m);
    try {
      sessionStorage.setItem(VIEWER_MODE_KEY, m);
    } catch {
      /* session-only nicety */
    }
  }, []);
  const [selectedId, setSelectedId] = useState(null); // focus-mode document
  // Per-document page counts (stream + focus share this map).
  const [numPagesByDoc, setNumPagesByDoc] = useState({});
  // Focus-mode PDF state
  const [pageNumber, setPageNumber] = useState(1);
  const [zoom, setZoom] = useState(1); // stream-wide AND focus
  // Focus-mode text state
  const [textContent, setTextContent] = useState(null);
  const [textLoading, setTextLoading] = useState(false);
  const [docError, setDocError] = useState("");
  // Expiry recovery — signed URLs last 15 min; on a stale-URL failure we
  // transparently re-fetch the listing ONCE and retry before erroring.
  const refreshedRef = useRef(false); // focus one-shot
  const refreshedDocsRef = useRef(new Set()); // stream: one-shot PER DOCUMENT
  const [streamFailed, setStreamFailed] = useState({});
  const [retryBump, setRetryBump] = useState(0);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(600);
  // Download popup + busy/error state (target = any document, from the
  // focus toolbar or a stream section affordance)
  const [downloadTargetId, setDownloadTargetId] = useState(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadMode, setDownloadMode] = useState("all");
  const [rangeFrom, setRangeFrom] = useState("1");
  const [rangeTo, setRangeTo] = useState("1");
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  // Full-packet merge state
  const [packetBusy, setPacketBusy] = useState(false);
  const [packetNotice, setPacketNotice] = useState("");
  // Sticky mini-header state (stream): current section + visible page
  const [activeDocId, setActiveDocId] = useState(null);
  const visiblePagesRef = useRef({});
  const [, setVisBump] = useState(0);
  const sectionRefs = useRef({});
  const streamScrollRef = useRef(0);

  const fetchDocs = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setError("");
      // Ticket C regression fix (2026-08-24): documents was evicted
      // from the batched /card payload because GCS V4 signed-URL
      // generation is CPU-bound (RSA per file) and holds the GIL —
      // stuffing it into the card hot path re-introduced loop
      // starvation. Documents now fetches from its own endpoint,
      // lazily on panel mount (unchanged from pre-C). Its cost is
      // paid only when the panel actually renders.
      try {
        const res = await axios.get(`${API}/api/intake/referrals/${fileId}/documents`, {
          headers: { Authorization: `Bearer ${getStoredToken()}` },
        });
        setData(res.data);
        return res.data;
      } catch (err) {
        console.error("Document listing failed:", err);
        setError(err.response?.data?.detail || "Failed to load documents");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [fileId]
  );

  // Auto-load on open — no button press; the backend fires documents_viewed
  // on this listing call exactly as before.
  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const documents = useMemo(() => data?.documents || [], [data]);
  const selected = documents.find((d) => d.id === selectedId) || documents[0] || null;
  const numPages = selected ? numPagesByDoc[selected.id] || null : null;

  // Reset focus per-document state on selection change
  useEffect(() => {
    setPageNumber(1);
    setTextContent(null);
    setDocError("");
  }, [selectedId, retryBump]);

  // Stale/expired signed URL (focus mode) → refresh listing once, retry.
  const handleExpiredResource = useCallback(async () => {
    if (refreshedRef.current) {
      setDocError("This document could not be loaded. Close and reopen the viewer to retry.");
      return;
    }
    refreshedRef.current = true;
    const fresh = await fetchDocs(true);
    if (fresh) {
      setRetryBump((b) => b + 1);
      setTimeout(() => {
        refreshedRef.current = false;
      }, 60000);
    } else {
      setDocError("This document could not be loaded. Close and reopen the viewer to retry.");
    }
  }, [fetchDocs]);

  // Stream: expiry one-shot PER DOCUMENT — a 403 on one document re-calls
  // the listing once (fresh signed URLs swap the file prop, react-pdf
  // retries that document); a second failure marks only that slot failed.
  const handleStreamDocError = useCallback(
    async (docId) => {
      if (refreshedDocsRef.current.has(docId)) {
        setStreamFailed((prev) => ({ ...prev, [docId]: true }));
        return;
      }
      refreshedDocsRef.current.add(docId);
      const fresh = await fetchDocs(true);
      if (!fresh) setStreamFailed((prev) => ({ ...prev, [docId]: true }));
    },
    [fetchDocs]
  );

  // Focus-mode text files (Email Body).
  useEffect(() => {
    if (mode !== "focus" || !selected || selected.content_type !== "text/plain") return undefined;
    let cancelled = false;
    setTextLoading(true);
    fetch(selected.url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((t) => {
        if (!cancelled) setTextContent(t);
      })
      .catch(() => {
        if (!cancelled) handleExpiredResource();
      })
      .finally(() => {
        if (!cancelled) setTextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, selected, handleExpiredResource]);

  // Keyboard: FOCUS-SCOPED (shared isFormFieldTarget guard) — never fires
  // while the user is typing in the adjacent detail panel. Stream: arrows
  // scroll by one page-height. Focus: arrows page the selected PDF.
  // Escape is handled by the WORKSPACE (back to list), not here.
  useEffect(() => {
    const onKey = (e) => {
      if (isFormFieldTarget(e)) return;
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) return;
      if (mode === "stream") {
        const el = containerRef.current;
        if (!el) return;
        const pageH = Math.round(containerWidth * zoom * PAGE_ASPECT);
        const dir = e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : -1;
        el.scrollBy({ top: dir * pageH, behavior: "smooth" });
        return;
      }
      if (!selected || selected.content_type !== "application/pdf" || !numPages) return;
      if (e.key === "ArrowLeft") setPageNumber((p) => Math.max(1, p - 1));
      if (e.key === "ArrowRight") setPageNumber((p) => Math.min(numPages, p + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, selected, numPages, containerWidth, zoom]);

  // Stable handler identity — only writes the per-doc page count, which
  // never feeds back into the <Document> file identity (no loop fuel).
  const handleDocLoadSuccess = useCallback(
    ({ numPages: n }) => {
      if (selected) setNumPagesByDoc((prev) => ({ ...prev, [selected.id]: n }));
    },
    [selected]
  );
  const handleSectionLoaded = useCallback((docId, n) => {
    setNumPagesByDoc((prev) => (prev[docId] === n ? prev : { ...prev, [docId]: n }));
  }, []);

  // ── Mode transitions ────────────────────────────────────────────────────
  const enterFocus = useCallback(
    (docId) => {
      streamScrollRef.current = containerRef.current?.scrollTop || 0;
      setSelectedId(docId);
      setModePersist("focus");
    },
    [setModePersist]
  );
  const backToStream = useCallback(() => {
    setModePersist("stream");
  }, [setModePersist]);
  // Returning to the stream restores the exact scroll position.
  useLayoutEffect(() => {
    if (mode === "stream" && containerRef.current) {
      containerRef.current.scrollTop = streamScrollRef.current;
    }
  }, [mode]);

  // Jump list (stream): smooth-scroll to the document's divider WITHOUT
  // touching selection state — no <Document> remounts.
  const jumpToSection = useCallback((docId) => {
    sectionRefs.current[docId]?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Sticky mini-header — IntersectionObserver on the section elements with
  // a top-band rootMargin: the section crossing the top of the viewport is
  // the current one.
  useEffect(() => {
    if (mode !== "stream" || loading || !containerRef.current) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        // pick the TOPMOST intersecting section (initial batch reports all)
        const hits = entries.filter((en) => en.isIntersecting);
        if (hits.length) {
          hits.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActiveDocId(hits[0].target.dataset.docid);
        }
      },
      { root: containerRef.current, rootMargin: "0px 0px -70% 0px" }
    );
    Object.values(sectionRefs.current).forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [mode, loading, documents.length]);

  const handlePageVisibility = useCallback((docId, pn, visible) => {
    const set = visiblePagesRef.current[docId] || (visiblePagesRef.current[docId] = new Set());
    if (visible) set.add(pn);
    else set.delete(pn);
    setVisBump((b) => b + 1);
  }, []);

  const activeDoc = documents.find((d) => d.id === activeDocId) || documents[0] || null;
  const activePages = activeDoc ? visiblePagesRef.current[activeDoc.id] : null;
  const activePage = activePages && activePages.size ? Math.min(...activePages) : null;

  // ── Download (client-side only; bytes never touch the backend) ──────────

  // Fire-and-forget audit: downloads are a larger PHI footprint than views,
  // so each one writes its own event — but the download NEVER blocks or
  // fails on audit latency. Always called, never awaited.
  const fireDownloadAudit = useCallback(
    (doc, pages) => {
      axios
        .post(
          `${API}/api/intake/referrals/${fileId}/documents/download-audit`,
          { document_name: doc.name, pages },
          { headers: { Authorization: `Bearer ${getStoredToken()}` } }
        )
        .catch(() => {});
    },
    [fileId]
  );

  // Fetch document bytes with the same one-shot expiry pattern as viewing:
  // 403 (expired signed URL) → re-call the documents endpoint once for
  // fresh URLs → retry once → then surface the error.
  const fetchBytesWithRefresh = useCallback(
    async (docId) => {
      const getDoc = (payload) => (payload?.documents || []).find((x) => x.id === docId);
      const doc = getDoc(data);
      if (!doc) throw new Error("Document not available");
      let res = await fetch(doc.url);
      if (res.status === 403) {
        const fresh = await fetchDocs(true);
        const freshDoc = getDoc(fresh);
        if (!freshDoc) throw new Error("Document no longer available");
        res = await fetch(freshDoc.url);
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.arrayBuffer();
    },
    [data, fetchDocs]
  );

  const downloadDoc = documents.find((d) => d.id === downloadTargetId) || null;
  const downloadDocPages = downloadDoc ? numPagesByDoc[downloadDoc.id] || null : null;
  const rangeFromNum = parseInt(rangeFrom, 10);
  const rangeToNum = parseInt(rangeTo, 10);
  // Range validation: 1 ≤ from ≤ to ≤ numPages, integers only.
  const rangeValid =
    Number.isInteger(rangeFromNum) &&
    Number.isInteger(rangeToNum) &&
    rangeFromNum >= 1 &&
    rangeFromNum <= rangeToNum &&
    !!downloadDocPages &&
    rangeToNum <= downloadDocPages;
  const rangeError =
    downloadMode === "range" && !rangeValid
      ? `Enter a valid range between 1 and ${downloadDocPages || "?"}`
      : "";

  const performDownload = useCallback(
    async (mode_) => {
      if (!downloadDoc) return;
      const pagesLabel = mode_ === "range" ? `${rangeFromNum}-${rangeToNum}` : "all";
      fireDownloadAudit(downloadDoc, pagesLabel);
      setDownloadBusy(true);
      setDownloadError("");
      try {
        const bytes = await fetchBytesWithRefresh(downloadDoc.id);
        if (mode_ === "range") {
          // pdf-lib assembly: copy exactly the selected pages (0-based
          // indices) into a fresh PDF, entirely client-side.
          const src = await PDFDocument.load(bytes);
          const out = await PDFDocument.create();
          const indices = [];
          for (let p = rangeFromNum; p <= rangeToNum; p += 1) indices.push(p - 1);
          const copied = await out.copyPages(src, indices);
          copied.forEach((pg) => out.addPage(pg));
          const outBytes = await out.save();
          const base = downloadDoc.name.replace(/\.pdf$/i, "");
          saveBlob(
            new Blob([outBytes], { type: "application/pdf" }),
            `${base} (pages ${rangeFromNum}-${rangeToNum}).pdf`
          );
        } else {
          // All pages: byte-identical original — NO pdf-lib re-assembly.
          saveBlob(
            new Blob([bytes], { type: downloadDoc.content_type || "application/octet-stream" }),
            downloadDoc.name
          );
        }
        setDownloadOpen(false);
      } catch (err) {
        console.error("Download failed:", err);
        setDownloadError("Download failed — please try again.");
      } finally {
        setDownloadBusy(false);
      }
    },
    [downloadDoc, rangeFromNum, rangeToNum, fireDownloadAudit, fetchBytesWithRefresh]
  );

  // PDFs get the range popup; non-PDFs download directly — no popup.
  // `docId` targets any document (stream per-section affordance or the
  // focus toolbar button).
  const onDownloadClick = useCallback(
    (docId) => {
      const doc = documents.find((d) => d.id === docId);
      if (!doc) return;
      setDownloadError("");
      setDownloadTargetId(docId);
      if (doc.content_type === "application/pdf") {
        setDownloadMode("all");
        setRangeFrom("1");
        setRangeTo(String(numPagesByDoc[docId] || 1));
        setDownloadOpen((o) => (downloadTargetId === docId ? !o : true));
      } else {
        // direct download with the resolved doc (state not yet flushed)
        setDownloadOpen(false);
        (async () => {
          const pagesLabel = "all";
          fireDownloadAudit(doc, pagesLabel);
          setDownloadBusy(true);
          try {
            const bytes = await fetchBytesWithRefresh(doc.id);
            saveBlob(
              new Blob([bytes], { type: doc.content_type || "application/octet-stream" }),
              doc.name
            );
          } catch (err) {
            console.error("Download failed:", err);
            setDownloadError("Download failed — please try again.");
          } finally {
            setDownloadBusy(false);
          }
        })();
      }
    },
    [documents, numPagesByDoc, downloadTargetId, fireDownloadAudit, fetchBytesWithRefresh]
  );

  // Reset download UI when the referral's documents change identity.
  useEffect(() => {
    setDownloadOpen(false);
    setDownloadError("");
    setPacketNotice("");
  }, [retryBump]);

  // ── Full-packet download — on-demand pdf-lib merge, prescription-first
  // order (server order), failed items skipped with a notice. NOTE: the
  // backend download-audit validator only admits 'all' | '{from}-{to}'
  // (backend is out of scope for this ticket), so the packet audit lands
  // as document_name="Document Packet", pages="all" — flagged in the
  // CHANGELOG for a later one-line widening to a literal "packet".
  const downloadPacket = useCallback(async () => {
    if (!documents.length) return;
    fireDownloadAudit({ name: "Document Packet" }, "all");
    setPacketBusy(true);
    setDownloadError("");
    setPacketNotice("");
    try {
      const items = [];
      const failures = [];
      for (const doc of documents) {
        try {
          if (doc.content_type === "text/plain") {
            const buf = await fetchBytesWithRefresh(doc.id);
            items.push({ kind: "text", name: doc.name, text: new TextDecoder().decode(buf) });
          } else if (doc.content_type === "application/pdf") {
            items.push({ kind: "pdf", name: doc.name, bytes: await fetchBytesWithRefresh(doc.id) });
          } else if (doc.content_type?.startsWith("image/")) {
            items.push({
              kind: "image",
              name: doc.name,
              contentType: doc.content_type,
              bytes: await fetchBytesWithRefresh(doc.id),
            });
          } else {
            failures.push(doc.name);
          }
        } catch {
          failures.push(doc.name);
        }
      }
      const { bytes, skipped } = await assemblePacket(items);
      const caseLabel = data?.patient_name || `Case ${fileId}`;
      saveBlob(new Blob([bytes], { type: "application/pdf" }), `${caseLabel} — Document Packet.pdf`);
      const allSkipped = [...failures, ...skipped];
      if (allSkipped.length) {
        setPacketNotice(`Couldn't include: ${allSkipped.join(", ")} — try downloading them individually`);
      }
    } catch (err) {
      console.error("Packet download failed:", err);
      setDownloadError("Download failed — please try again.");
    } finally {
      setPacketBusy(false);
    }
  }, [documents, data, fileId, fireDownloadAudit, fetchBytesWithRefresh]);

  // Fit page width to the viewer area.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = Math.max(320, Math.floor(el.clientWidth) - 48);
      // Oscillation guard: a scrollbar appearing/disappearing shifts
      // clientWidth by ~15px; feeding that straight into <Page width>
      // re-renders the canvas, toggles the scrollbar back, and loops
      // (rapid flashing). Only accept meaningful size changes.
      setContainerWidth((prev) => (Math.abs(prev - w) > 24 ? w : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, mode]);

  const registerSectionRef = useCallback((docId) => (el) => {
    sectionRefs.current[docId] = el;
  }, []);

  const isPdf = selected?.content_type === "application/pdf";
  const isImage = selected?.content_type?.startsWith("image/");
  const isText = selected?.content_type === "text/plain";
  const pageWidth = Math.round(containerWidth * zoom);

  return (
    <div
      className="h-full w-full bg-white flex flex-col overflow-hidden"
      data-testid="document-viewer-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex-shrink-0">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-slate-900 truncate" data-testid="doc-viewer-patient">
            {data?.patient_name || "Documents"}
          </h2>
          <p className="text-[11px] text-slate-500 truncate">Case {fileId}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center gap-2 text-slate-400" data-testid="doc-viewer-loading">
          <Loader2 className="animate-spin" size={20} />
          <span className="text-sm">Loading documents…</span>
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center" data-testid="doc-viewer-error">
          <div className="text-center">
            <AlertTriangle size={24} className="text-rose-500 mx-auto mb-2" />
            <p className="text-sm text-slate-700">{error}</p>
          </div>
        </div>
      ) : documents.length === 0 ? (
        <div className="flex-1 flex items-center justify-center" data-testid="doc-viewer-empty">
          <div className="text-center">
            <FolderOpen size={28} className="text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-600">No documents found for this referral</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Toolbar: mode toggle, jump list / switcher, zoom, focus pager,
              per-doc download, full-packet download */}
          <div className="relative flex items-center gap-2 px-3 py-2 bg-white border-b border-slate-200 flex-shrink-0" data-testid="doc-switcher">
            <button
              onClick={() => (mode === "stream" ? enterFocus((selected || documents[0]).id) : backToStream())}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-600 flex-shrink-0"
              data-testid="viewer-mode-toggle"
              aria-label={mode === "stream" ? "Switch to single-document focus" : "Back to continuous scroll"}
              title={mode === "stream" ? "Focus one document" : "Back to continuous scroll"}
            >
              {mode === "stream" ? <Maximize2 size={14} /> : <Rows3 size={14} />}
            </button>
            {typeIcon((mode === "stream" ? activeDoc : selected)?.content_type)}
            <select
              value={(mode === "stream" ? activeDoc?.id : selected?.id) || ""}
              onChange={(e) =>
                mode === "stream" ? jumpToSection(e.target.value) : setSelectedId(e.target.value)
              }
              className="flex-1 min-w-0 text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-800"
              data-testid="doc-switcher-select"
              aria-label={mode === "stream" ? "Jump to document" : "Switch document"}
            >
              {documents.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
              data-testid="pdf-zoom-out"
              aria-label="Zoom out"
            >
              <ZoomOut size={14} />
            </button>
            <button
              onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
              data-testid="pdf-zoom-in"
              aria-label="Zoom in"
            >
              <ZoomIn size={14} />
            </button>
            {mode === "focus" && isPdf && numPages && (
              <div className="flex items-center gap-1 flex-shrink-0" data-testid="pdf-pager">
                <button
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  disabled={pageNumber <= 1}
                  className="p-1.5 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-30"
                  data-testid="pdf-prev-page"
                  aria-label="Previous page"
                >
                  <ChevronLeft size={15} />
                </button>
                <span className="text-xs text-slate-600 whitespace-nowrap" data-testid="pdf-page-indicator">
                  Page {pageNumber} of {numPages}
                </span>
                <button
                  onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
                  disabled={pageNumber >= numPages}
                  className="p-1.5 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-30"
                  data-testid="pdf-next-page"
                  aria-label="Next page"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            )}
            {mode === "focus" && (
              <button
                onClick={() => selected && onDownloadClick(selected.id)}
                disabled={!selected || downloadBusy}
                className="p-1.5 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-30 flex-shrink-0"
                data-testid="doc-download-btn"
                aria-label="Download document"
                title="Download"
              >
                {downloadBusy ? <Loader2 className="animate-spin" size={15} /> : <Download size={15} />}
              </button>
            )}
            {/* Download full packet — on-demand pdf-lib merge of every
                loadable document, prescription-first. */}
            <button
              onClick={downloadPacket}
              disabled={packetBusy || downloadBusy}
              className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-[11px] font-medium text-slate-700 hover:bg-slate-100 border border-slate-200 disabled:opacity-40 flex-shrink-0"
              data-testid="packet-download-btn"
              aria-label="Download full packet"
              title="Download all documents as one PDF"
            >
              {packetBusy ? <Loader2 className="animate-spin" size={13} /> : <Package size={13} />}
              {packetBusy ? "Merging…" : "Full packet"}
            </button>

            {downloadOpen && downloadDoc?.content_type === "application/pdf" && (
              <div
                className="absolute right-2 top-11 z-50 w-64 bg-white border border-slate-200 rounded-lg shadow-xl p-3 space-y-2"
                data-testid="download-popover"
              >
                <p className="text-xs font-semibold text-slate-700 truncate">Download {downloadDoc.name}</p>
                <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                  <input
                    type="radio"
                    name="download-mode"
                    checked={downloadMode === "all"}
                    onChange={() => setDownloadMode("all")}
                    data-testid="download-mode-all"
                  />
                  All pages (original file)
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                  <input
                    type="radio"
                    name="download-mode"
                    checked={downloadMode === "range"}
                    onChange={() => setDownloadMode("range")}
                    data-testid="download-mode-range"
                  />
                  Page range
                </label>
                {downloadMode === "range" && (
                  <div className="flex items-center gap-2 pl-5">
                    <input
                      type="number"
                      min={1}
                      max={downloadDocPages || undefined}
                      value={rangeFrom}
                      onChange={(e) => setRangeFrom(e.target.value)}
                      className="w-16 text-xs border border-slate-300 rounded px-1.5 py-1"
                      data-testid="download-range-from"
                      aria-label="From page"
                    />
                    <span className="text-xs text-slate-500">to</span>
                    <input
                      type="number"
                      min={1}
                      max={downloadDocPages || undefined}
                      value={rangeTo}
                      onChange={(e) => setRangeTo(e.target.value)}
                      className="w-16 text-xs border border-slate-300 rounded px-1.5 py-1"
                      data-testid="download-range-to"
                      aria-label="To page"
                    />
                    <span className="text-[10px] text-slate-400">of {downloadDocPages || "?"}</span>
                  </div>
                )}
                {rangeError && (
                  <p className="text-[11px] text-rose-600 pl-5" data-testid="download-range-error">
                    {rangeError}
                  </p>
                )}
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    onClick={() => setDownloadOpen(false)}
                    className="px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded"
                    data-testid="download-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => performDownload(downloadMode)}
                    disabled={downloadBusy || (downloadMode === "range" && !rangeValid)}
                    className="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                    data-testid="download-confirm-btn"
                  >
                    {downloadBusy ? "Preparing download…" : "Download"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {downloadError && (
            <div className="px-3 py-1.5 bg-rose-50 border-b border-rose-200 text-[11px] text-rose-700 flex-shrink-0" data-testid="download-error">
              {downloadError}
            </div>
          )}
          {packetNotice && (
            <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-700 flex-shrink-0" data-testid="packet-skip-notice">
              {packetNotice}
            </div>
          )}

          {/* Sticky mini-header (stream) — current section + page */}
          {mode === "stream" && activeDoc && (
            <div
              className="px-3 py-1 bg-slate-800/90 text-white text-[11px] font-medium flex-shrink-0 truncate"
              data-testid="stream-sticky-header"
            >
              {activeDoc.name}
              {activeDoc.content_type === "application/pdf" && numPagesByDoc[activeDoc.id]
                ? ` — page ${activePage || 1} of ${numPagesByDoc[activeDoc.id]}`
                : ""}
            </div>
          )}

          {mode === "stream" ? (
            // Continuous scroll: every document stacked in ONE scroll
            // container, prescription-first server order, progressive —
            // each section renders as soon as its own fetch completes.
            <div
              ref={containerRef}
              className="flex-1 overflow-auto px-4 pb-6 bg-slate-100 [scrollbar-gutter:stable]"
              data-testid="doc-stream-container"
            >
              {documents.map((doc) => (
                <DocSection
                  key={doc.id}
                  doc={doc}
                  width={pageWidth}
                  numPages={numPagesByDoc[doc.id]}
                  failed={!!streamFailed[doc.id]}
                  onLoaded={handleSectionLoaded}
                  onExpired={handleStreamDocError}
                  onEnterFocus={enterFocus}
                  onDownload={onDownloadClick}
                  sectionRef={registerSectionRef(doc.id)}
                  onPageVisibility={handlePageVisibility}
                  downloadBusy={downloadBusy || packetBusy}
                />
              ))}
            </div>
          ) : (
            <div
              ref={containerRef}
              className="flex-1 overflow-auto p-4 flex justify-center bg-slate-100 [scrollbar-gutter:stable]"
              data-testid="doc-viewer-canvas"
            >
              {docError ? (
                <div className="self-center text-center" data-testid="doc-render-error">
                  <AlertTriangle size={22} className="text-rose-500 mx-auto mb-2" />
                  <p className="text-sm text-slate-700">{docError}</p>
                </div>
              ) : !selected ? null : isPdf ? (
                <Document
                  key={`${selected.id}-${retryBump}`}
                  // file is the signed-URL STRING — identity changes only
                  // when the URL actually changes (fresh signing). No options
                  // prop is passed; if one is ever needed it must be a
                  // module-level constant, never inline.
                  file={selected.url}
                  onLoadSuccess={handleDocLoadSuccess}
                  onLoadError={handleExpiredResource}
                  loading={PDF_LOADING_ELEMENT}
                >
                  <Page
                    pageNumber={pageNumber}
                    width={Math.round(containerWidth * zoom)}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="shadow-md"
                  />
                </Document>
              ) : isImage ? (
                <img
                  key={`${selected.id}-${retryBump}`}
                  src={selected.url}
                  alt={selected.name}
                  onError={handleExpiredResource}
                  className="max-w-full max-h-full object-contain self-center shadow-md rounded"
                  data-testid="doc-image"
                />
              ) : isText ? (
                textLoading ? (
                  <div className="self-center flex items-center gap-2 text-slate-400 text-sm">
                    <Loader2 className="animate-spin" size={16} /> Loading…
                  </div>
                ) : (
                  <pre
                    className="w-full max-w-4xl bg-white border border-slate-200 rounded-lg p-4 text-xs font-mono text-slate-800 whitespace-pre-wrap overflow-auto"
                    data-testid="doc-text-content"
                  >
                    {textContent ?? ""}
                  </pre>
                )
              ) : (
                <div className="self-center text-center" data-testid="doc-unsupported">
                  <FileIcon size={26} className="text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-700 mb-3">{selected.name}</p>
                  {/* Unified with the toolbar Download button — same audited
                      client-side path, no separate anchor download. */}
                  <button
                    onClick={() => onDownloadClick(selected.id)}
                    disabled={downloadBusy}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    data-testid="doc-download-unsupported-btn"
                  >
                    <Download size={15} /> {downloadBusy ? "Preparing download…" : "Download"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentViewerPanel;
